import "./ReportProgressChartData.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact, {
  HighchartsReactRefObject
} from "highcharts-react-official";
import { JobPricingContext } from "../../../store/JobPricingContext";
import { RootState } from "../../../store/reducers";
import { JobPricingReportDetails } from "../../../helpers/models/JobPricingDetailModels";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import { GradientCircularProgress } from "../../../store/slices/jobPricingDetailsSlice";
import { useTranslation } from "react-i18next";

const BLUE_BAR_CHART_COLOR = "#54A0FF";
const GREEN_BAR_CHART_COLOR = "#00C29B";

export const ReportProgressChartData = () => {
  const { t } = useTranslation();
  const { setBarChartReference } = useContext(JobPricingContext);
  const { reportDetails, isReportDetailsLoading } = useSelector(
    (state: RootState) => state.jobPricingDetails
  );
  const PROGRESS_CHART_SERIES_OPTIONS = {
    type: "bar",
    name: "",
    data: [],
    animation: {
      duration: 250
    },
    enableMouseTracking: false,
    pointWidth: 15,
    borderWidth: 0,
    borderRadius: "50%"
  } as Highcharts.SeriesBarOptions;
  const PROGRESS_CHART_OPTIONS = {
    title: {
      text: "",
      margin: 0
    },
    chart: {
      type: "bar",
      width: 440,
      height: 150
    },
    credits: {
      enabled: false
    },
    lang: {
      noData: JobPricingReportConstants.NO_DATA_AVAILABLE_MSG
    },
    tooltip: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
    xAxis: {
      visible: false
    },
    yAxis: {
      visible: false
    },
    plotOptions: {
      series: {
        stacking: "normal",
        enableMouseTracking: false
      },
      bar: {
        states: {
          inactive: {
            enabled: false
          }
        }
      }
    },
    legend: {
      layout: "vertical",
      reversed: true,
      itemMarginBottom: 20,
      symbolHeight: 9,
      useHTML: true,
      events: {
        itemClick: function () {
          return false;
        }
      },
      labelFormatter: function () {
        return formatCustomLengend(this);
      }
    },
    colors: [GREEN_BAR_CHART_COLOR, BLUE_BAR_CHART_COLOR].reverse(),
    series: [],
    exporting: {
      enabled: false,
      chartOptions: {
        title: {
          text: t("pa.fixedVariableCompensationDistribution"),
          margin: 30
        },
        chart: {
          width: 500,
          height: 400,
          style: {
            fontFamily: "Lato",
            fontSize: "16px"
          }
        },
        legend: {
          y: -100,
          itemStyle: {
            fontSize: "16px"
          }
        }
      }
    }
  } as Highcharts.Options;
  const [progressChartOptions, setProgressChartOptions] =
    useState<Highcharts.Options>(PROGRESS_CHART_OPTIONS);
  const [targetTotalCash, setTargetTotalCash] = useState<string | null>(null);
  const barChartComponent = useRef<HighchartsReactRefObject>(null);

  const formatCustomLengend = (
    context: Highcharts.Point | Highcharts.Series
  ) => {
    let customPercentage = "";
    if (context.options && context.options.custom) {
      customPercentage = context.options.custom["percentage"] || 0;
    }
    return `<span class="highcharts-legend-item-custom">
      <span>${context.name}</span>
      <span>
        <b>${customPercentage}%</b>
      </span>
    </span>`;
  };

  const setChartSeriesOption = () => {
    const chartSeriesOptions: Highcharts.SeriesBarOptions[] = [];
    let totalCash = 0;
    reportDetails
      .filter((data: JobPricingReportDetails, index: number) => index < 2)
      .forEach((data: JobPricingReportDetails, index: number) => {
        totalCash += Number(data.targetMarket);
      });
    setTargetTotalCash(totalCash ? totalCash.toLocaleString() : "-");
    reportDetails
      .filter((data: JobPricingReportDetails, index: number) => index < 2)
      .forEach((data: JobPricingReportDetails, index: number) => {
        const seriesOption: Highcharts.SeriesBarOptions = {
          ...PROGRESS_CHART_SERIES_OPTIONS
        };
        const percentage =
          Number(data.targetMarket) && totalCash
            ? Math.round((Number(data.targetMarket) / totalCash) * 100)
            : 0;
        seriesOption.name = data.factName;
        seriesOption.custom = {
          value: Number(data.targetMarket).toLocaleString(),
          percentage
        };
        if (totalCash) {
          if (index === 0) {
            seriesOption.data = [
              {
                y: -Number(percentage || 1.5),
                className: !percentage ? "blue-bar-color" : ""
              }
            ];
          } else {
            seriesOption.data = [
              {
                y: Number(percentage) || 1.5,
                className: !percentage ? "green-bar-color" : ""
              }
            ];
          }
        } else {
          seriesOption.data = [0];
        }
        chartSeriesOptions.push(seriesOption);
      });
    progressChartOptions.series = chartSeriesOptions.reverse();
    progressChartOptions.exporting = {
      ...progressChartOptions.exporting,
      chartOptions: {
        ...(progressChartOptions.exporting || {}).chartOptions,
        subtitle: {
          text: totalCash ? totalCash.toLocaleString() : "-",
          y: 80,
          style: {
            fontSize: "22px",
            fontWeight: "600",
            color: "#243D42"
          }
        }
      }
    };
    setProgressChartOptions({ ...progressChartOptions });
  };

  useEffect(() => {
    if (barChartComponent && barChartComponent.current) {
      const barChartRef = barChartComponent.current.chart;
      setBarChartReference(barChartRef);
    }
  }, [barChartComponent, progressChartOptions]);

  useEffect(() => {
    if (reportDetails && reportDetails.length) {
      setChartSeriesOption();
    } else {
      setTargetTotalCash(null);
      progressChartOptions.series = [];
      setProgressChartOptions({ ...progressChartOptions });
    }
  }, [reportDetails]);

  return isReportDetailsLoading ? (
    <GradientCircularProgress />
  ) : (
    <div className="sjpr-progresschart-data-container">
      <Typography variant="body2" sx={{ textAlign: "center" }}>
        {t("pa.fixedVariableCompensationDistribution")}
      </Typography>
      {reportDetails && reportDetails.length > 0 ? (
        <>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", marginTop: 2, marginBottom: 2 }}
          >
            <b>{targetTotalCash}</b>
          </Typography>
          <div className="progress-chart-container">
            <HighchartsReact
              highcharts={Highcharts}
              options={progressChartOptions}
              ref={barChartComponent}
            />
          </div>
          <div className="text-ods-font-styles-body-regular-xsmall text-ods-color-components-text-brand-neutral-light text-center">
            {t("pa.fixedVariableNote")}
          </div>
        </>
      ) : (
        <div className="chart-no-data">
          <Typography variant="body2">
            {JobPricingReportConstants.NO_DATA_AVAILABLE_MSG}
          </Typography>
        </div>
      )}
    </div>
  );
};
